import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
	Drawer,
	Box,
	List,
	ListItemText,
	ListItemButton,
	Collapse,
	IconButton,
	Typography,
	Divider,
	CircularProgress,
} from "@mui/material";
import { usePageContext, type Page } from "../context/PageContext";
import { useAuth } from "../context/AuthContext";
import { ExpandLess, ExpandMore, Logout } from "@mui/icons-material";
import UserAvatar from "./UserAvatar";

const appBarHeight = 64;

const Sidebar: React.FC = () => {
	const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});
	const { setSelectedPageId, pages, pagesLoading } = usePageContext();
	const { userName } = useAuth();
	const { selectedHeaderIndex, setSelectedHeaderIndex } = usePageContext();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const handleToggle = (pageId: string) => {
		setOpenItems((prevState) => ({
			...prevState,
			[pageId]: !prevState[pageId],
		}));
	};

	const handleLogout = () => {
		localStorage.removeItem("rooster-support-token");
		localStorage.removeItem("selectedPageId");
		window.close();
	};

	const renderPages = (pages: Page[], indent = 0) => (
		<List sx={{ flexGrow: 1 }}>
			{pages.map((page) => (
				<React.Fragment key={page.id}>
					<ListItemButton
						onClick={() => {
							const hasChildren = page.children && page.children.length > 0;
							!hasChildren && setSelectedPageId(page.id);
							setSelectedHeaderIndex(Number.parseInt(page.id));
						}}
						sx={{ ml: indent }}
						selected={selectedHeaderIndex === Number.parseInt(page.id)}
					>
						<ListItemText
							primary={page.title}
							onClick={() => {
								page.children &&
									page.children.length > 0 &&
									handleToggle(page.id);
							}}
						/>
						{page.children && page.children.length > 0 && (
							<IconButton onClick={() => handleToggle(page.id)} edge="start">
								{openItems[page.id] ? <ExpandLess /> : <ExpandMore />}
							</IconButton>
						)}
					</ListItemButton>
					{page.children && page.children.length > 0 && (
						<Collapse in={openItems[page.id]} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
								{renderPages(page.children, indent + 2)}
							</List>
						</Collapse>
					)}
				</React.Fragment>
			))}
		</List>
	);

	if (isMobile) {
		return <></>;
	}

	return (
		<Drawer
			variant="permanent"
			open={true}
			sx={{
				width: { xl: "15%", sm: "30%" },
				flexShrink: 0,
				"& .MuiDrawer-paper": {
					width: { xl: "15%", sm: "30%" },
					marginTop: `${appBarHeight}px`,
					display: "flex",
					flexDirection: "column",
				},
				"@media print": {
					display: "none",
				},
			}}
		>
			<Box
				sx={{
					overflowY: "auto",
					overflowX: "hidden",
					display: "flex",
					flexDirection: "column",
					height: { xl: "85vh", lg: "85vh", md: "calc(100vh - 52%)" },
				}}
			>
				{pagesLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
					<CircularProgress size={28} />
				</Box>
			) : (
				renderPages(pages)
			)}
			</Box>

			{userName && (
				<>
					<Divider
						sx={{
							mb: 2,
						}}
					/>

					<Box
						sx={{
							ml: 2,
							display: "flex",
							alignItems: "center",
						}}
					>
						<div style={{ display: "flex", alignItems: "center" }}>
							<UserAvatar />
							<Typography variant="body1" sx={{ ml: 1 }}>
								{userName}
							</Typography>
						</div>

						<Box sx={{ flexGrow: 1 }} />
						<IconButton color="error" onClick={handleLogout} sx={{ mt: 1 }}>
							<Logout />
						</IconButton>
					</Box>
				</>
			)}
		</Drawer>
	);
};

export default Sidebar;
